<template>
    <div class="layout-px-spacing">
        <teleport to="#breadcrumb">
            <ul class="navbar-nav flex-row">
                <li>
                    <div class="page-header">
                        <nav class="breadcrumb-one" aria-label="breadcrumb">
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item"><a href="javascript:;">HR Actions</a></li>
                                <li class="breadcrumb-item active" aria-current="page"><span>Violation Details</span>
                                </li>
                            </ol>
                        </nav>
                    </div>
                </li>
            </ul>
        </teleport>

        <div class="row layout-top-spacing">
            <div class="col-xl-12 col-lg-12 col-md-12 col-12 layout-spacing">
                <div class="panel br-4">
                    <div class="panel-body">
                        <div v-if="isLoading" class="text-center py-4">
                            <div class="spinner-border text-primary" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </div>

                        <template v-else>
                            <div class="d-flex justify-content-between align-items-center mb-4">
                                <h4>Violation Details</h4>
                                <button v-if="hasPrintHRActionPermission && HRTakenAction"
                                    class="btn btn-primary"
                                    @click="printReport">
                                    Print Report
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                        stroke-linejoin="round" class="feather feather-printer">
                                        <polyline points="6 9 6 2 18 2 18 9"></polyline>
                                        <path
                                            d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2">
                                        </path>
                                        <rect x="6" y="14" width="12" height="8"></rect>
                                    </svg>
                                </button>
                                <button @click="$router.go(-1)" class="btn btn-secondary">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                        stroke-linejoin="round" class="feather feather-arrow-left">
                                        <line x1="19" y1="12" x2="5" y2="12"></line>
                                        <polyline points="12 19 5 12 12 5"></polyline>
                                    </svg>
                                    Back
                                </button>
                            </div>

                            <div class="row">
                                <!-- Violation Details -->
                                <div class="col-md-6">
                                    <div class="card mb-4">
                                        <div class="card-header bg-outline-info text-white">
                                            <h5 class="mb-0">Violation Information</h5>
                                        </div>
                                        <div class="card-body">
                                            <div class="table-responsive">
                                                <table class="table table-bordered">
                                                    <tbody>
                                                        <tr>
                                                            <th width="30%">Violation Type</th>
                                                            <td>{{ violationAction.violation?.title || 'N/A' }}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>Description</th>
                                                            <td>{{ violationAction.violation?.description || 'N/A' }}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <th>Bus Registration</th>
                                                            <td>{{ violationAction.bus_registration || 'N/A' }}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>Driver</th>
                                                            <td>{{ getDriverName(violationAction.driver_id) || 'N/A' }}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <th>Conductor</th>
                                                            <td>{{ violationAction.conductor_id || 'N/A' }}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>Route</th>
                                                            <td>{{ violationAction.route || 'N/A' }}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>Schedule</th>
                                                            <td>{{ violationAction.schedule || 'N/A' }}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>Reported By</th>
                                                            <td>{{ violationAction.user?.username || 'N/A' }}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>Reported At</th>
                                                            <td>{{ formatDateTime(violationAction.created_at) }}</td>
                                                        </tr>
                                                        <template v-if="violationAction.parameters">
                                                            <tr v-for="(value, key) in violationAction.parameters"
                                                                :key="key">
                                                                <th>{{ key }}</th>
                                                                <td>{{ value }}</td>
                                                            </tr>
                                                        </template>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- HR Action Section -->
                                <div class="col-md-6">
                                    <div class="card mb-4">
                                        <div class="card-header bg-outline-primary text-white">
                                            <h5 class="mb-0">HR Action</h5>
                                        </div>
                                        <div class="card-body">
                                            <form @submit.prevent="submitHrAction">
                                                <div class="mb-3">
                                                    <label class="form-label">Status</label>
                                                    <select v-model="hrActionForm.status" class="form-select" required>
                                                        <option value="Pending">Pending</option>
                                                        <option value="Processing">Processing</option>
                                                        <option value="Completed">Completed</option>
                                                    </select>
                                                </div>

                                                <!-- Actions Taken -->
                                                <div class="mb-3">
                                                    <label class="form-label">Actions Taken</label>
                                                    <multiselect v-model="hrActionForm.taken_action_id"
                                                        :options="filteredActionsTaken" :searchable="true"
                                                        :show-labels="false" :close-on-select="true"
                                                        :placeholder="'Select actions taken...'" track-by="id"
                                                        label="name" :custom-label="action => action.name"
                                                        @search-change="actionsTakenSearch = $event" />
                                                </div>

                                                <!-- Consolidated Amount -->
                                                <div class="mb-3">
                                                    <label class="form-label">Consolidated Amount</label>
                                                    <input type="number" v-model="hrActionForm.consolidated_amount"
                                                        class="form-control" placeholder="Enter consolidated amount"
                                                        min="0" step="1" />
                                                </div>

                                                <div class="mb-3">
                                                    <label class="form-label">Remarks</label>
                                                    <textarea v-model="hrActionForm.remarks" class="form-control"
                                                        rows="4"
                                                        placeholder="Enter HR remarks about this violation..."></textarea>
                                                </div>

                                                <div class="mb-3">
                                                    <label class="form-label">Upload Document</label>
                                                    <input type="file" ref="documentInput" class="form-control"
                                                        @change="handleDocumentUpload" accept=".pdf,.jpg,.png">
                                                    <small class="text-muted">Upload signed documents or evidence (PDF,
                                                        JPG, PNG)</small>
                                                </div>

                                                <div v-if="hrAction.document_path" class="mb-3">
                                                    <label class="form-label">Current Document</label>
                                                    <div class="d-flex align-items-center">
                                                        <a :href="hrAction.document_url" target="_blank" class="me-3">
                                                            <i class="fas fa-file-pdf fa-2x text-danger"
                                                                v-if="hrAction.document_path.endsWith('.pdf')"></i>
                                                            <i class="fas fa-file-image fa-2x text-primary" v-else></i>
                                                            View Document
                                                        </a>
                                                        <button type="button" @click="removeDocument"
                                                            class="btn btn-sm btn-outline-danger">
                                                            <i class="fas fa-trash-alt"></i> Remove
                                                        </button>
                                                    </div>
                                                </div>

                                                <div class="d-flex justify-content-between">
                                                    <button type="button" @click="cancelHrAction"
                                                        class="btn btn-secondary">
                                                        Cancel
                                                    </button>
                                                    <button type="submit" class="btn btn-primary"
                                                        :disabled="isSubmitting">
                                                        <span v-if="isSubmitting"
                                                            class="spinner-border spinner-border-sm me-1"></span>
                                                        {{ hrAction.id ? 'Update' : 'Submit' }} HR Action
                                                    </button>
                                                </div>
                                            </form>
                                        </div>
                                    </div>

                                    <!-- Action History -->
                                    <div class="card" v-if="actionHistory.length > 0">
                                        <div class="card-header bg-outline-primary text-white">
                                            <h5 class="mb-0">Action History</h5>
                                        </div>
                                        <div class="card-body">
                                            <div class="timeline">
                                                <div v-for="(history, index) in actionHistory" :key="index"
                                                    class="timeline-item">
                                                    <div class="timeline-point"></div>
                                                    <div class="timeline-content">
                                                        <div class="timeline-date">{{ formatDateTime(history.created_at)
                                                        }}</div>
                                                        <div class="timeline-title">{{ history.user?.username ||
                                                            'System' }}</div>
                                                        <div class="timeline-text">
                                                            <p v-if="history.remarks">{{ history.remarks }}</p>
                                                            <p v-if="history.document_path">
                                                                <a :href="getDocumentUrl(history.document_path)"
                                                                    target="_blank">
                                                                    <i class="fas fa-paperclip me-2"></i>View attached
                                                                    document
                                                                </a>

                                                                <!-- Download Document Button -->
                                                                <button @click="downloadDocument(history.id)"
                                                                    class="btn btn-sm btn-outline-info">
                                                                    Download
                                                                </button>
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import { BASE_URL, BASE_URL_3, BASE_URL_5 } from '@/Config/config';
import Swal from 'sweetalert2';
import { useStore } from 'vuex';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';

const store = useStore();
const route = useRoute();
const router = useRouter();

// Reactive variables
const documentInput = ref(null);
const isLoading = ref(true);
const isSubmitting = ref(false);
const violationAction = ref({});
const hrAction = ref({});
const actionHistory = ref([]);
const drivers = ref([]);
const actionsTakenList = ref([]);

// Search Variables
const actionsTakenSearch = ref('');

const showErrorAlert = (message) => {
    Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: message,
        padding: '2em',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK'
    });
};

const showSuccessAlert = (message) => {
    Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: message,
        padding: '2em',
        confirmButtonColor: '#3085d6',
        timer: 3000
    });
};

const hrActionForm = ref({
    status: 'Pending',
    taken_action_id: '',
    consolidated_amount: '',
    remarks: '',
    document: null
});

// Permission Check
const hasDownloadPermission = computed(() => {
    return store.getters.hasPermission('download_hr_actions');
});

const hasUploadPermission = computed(() => {
    return store.getters.hasPermission('upload_hr_actions');
});

const hasCreatePermission = computed(() => {
    return store.getters.hasPermission('create_hr_actions');
});

const hasViewPermission = computed(() => {
    return store.getters.hasPermission('view_hr_actions');
});

const hasViewActionsTakenPermission = computed(() => {
    return store.getters.hasPermission('view_taken_actions');
});

const hasPrintHRActionPermission = computed(() => {
    return store.getters.hasPermission('print_hr_action');
});

const HRTakenAction = computed(() => {
    return violationAction.value?.status === 'Completed';
});

// Get username from store
const username = computed(() => {
    const user = store.state.user;
    return user?.username || user?.name || 'User';
});

const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const downloadDocument = async (id) => {
    try {
        // Check permission before downloading
        if (!hasDownloadPermission.value) {
            showErrorAlert("You don't have permission to download documents");
            return;
        }

        const loading = Swal.fire({
            title: 'Preparing download',
            allowOutsideClick: false,
            showConfirmButton: false,
            willOpen: () => Swal.showLoading()
        });

        const response = await axios.get(
            `${BASE_URL}/abs-violation-actions/hr-actions/download/${id}`,
            {
                responseType: 'blob',
                headers: {
                    'Authorization': `Bearer ${store.state.token}`,
                    'X-Company-ID': store.state.currentCompany?.id
                }
            }
        );

        const mimeType = response.headers['content-type'];
        const extension = mimeType.split('/')[1] || 'bin'; // e.g., 'jpeg', 'pdf', 'docx'
        const filename = `document_${id}.${extension}`;

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();

        loading.close();
        console.log(`Download successful: ${filename}`);
        console.log('Content-Disposition:', response.headers['content-disposition']);
        console.log('Content-Type:', mimeType);

    } catch (error) {
        loading.close();
        console.error('Download error:', error);

        if (error.response?.status === 401) {
            showErrorAlert('Session expired. Please login again.');
            router.push('/login');
        } else {
            showErrorAlert(error.response?.data?.message || 'Download failed');
        }
    }
};

// Update your existing getDocumentUrl method to ensure proper URL
const getDocumentUrl = (path) => {
    // Check permission before accessing document URL
    if (!hasDownloadPermission.value) {
        showErrorAlert("You don't have permission to view documents");
        return;
    }
    if (!path) return '#';
    // If path already has full URL, return as-is
    if (path.startsWith('http')) return path;
    // Otherwise construct from base URL
    return `${BASE_URL_5}/storage/${path}`;
};

const handleDocumentUpload = (event) => {
    // Check permission before uploading document
    if (!hasUploadPermission.value) {
        showErrorAlert("You don't have permission to upload documents");
        return;
    }

    const file = event.target.files[0];
    if (file) {
        hrActionForm.value.document = file;
    }
};

const removeDocument = () => {
    hrActionForm.value.document = null;
    if (documentInput.value) {
        documentInput.value.value = '';
    }
};

const loadViolationAction = async () => {
    try {
        // Check permission before loading violation action
        if (!hasViewPermission.value) {
            showErrorAlert("You don't have permission to view violation actions");
            return;
        }

        const response = await axios.get(`${BASE_URL}/abs-violation-actions/show/${route.params.id}`, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });
        violationAction.value = response.data.data || response.data;
        console.log('Violation Action responded:', response.status);

        // Load existing HR action if exists
        await loadHrAction();
    } catch (error) {
        console.error('Error loading violation action:', error);
        showErrorAlert(error.response?.data?.message || 'Failed to load violation action');
        // Redirect back if error occurs
        // router.go(-1);
    } finally {
        isLoading.value = false;
    }
};

const fetchActionsTaken = async () => {
    try {
        // Check permission before fetching actions taken
        if (!hasViewActionsTakenPermission.value) {
            showErrorAlert("You don't have permission to view actions taken");
            return;
        }

        const response = await axios.get(`${BASE_URL}/taken-actions`, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });
        actionsTakenList.value = response.data.data || response.data;
        console.log('Actions taken loaded:', actionsTakenList.value);
    } catch (error) {
        console.error('Error fetching actions taken:', error);
        showErrorAlert('Failed to load actions taken. Please try again later.');
    }
};

const filteredActionsTaken = computed(() => {
    if (!actionsTakenSearch.value) return actionsTakenList.value;
    const term = actionsTakenSearch.value.toLowerCase();
    return actionsTakenList.value.filter(action =>
        action.name.toLowerCase().includes(term))
});

const formatParameterKey = (key) => {
    // Convert camelCase or snake_case to Title Case
    return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/_/g, ' ')
        .replace(/^./, str => str.toUpperCase())
        .trim();
};

const loadDrivers = async () => {
    try {
        const response = await axios.get(`${BASE_URL_3}/company/employees/list?company_id=2`);
        drivers.value = response.data.employees || response.data;
        console.log("Drivers API response:", response.status);
    } catch (error) {
        console.error('Error fetching employees:', error);
        showErrorAlert(error.response?.data?.message || 'Failed to load drivers');
    }
};

// Map IDs to names
const getDriverName = (driverId) => {
    // Convert driverId to number if it's stored as string in violation actions
    const idToFind = typeof driverId === 'string' ? parseInt(driverId) : driverId;

    // Find the driver in the drivers list
    const driver = drivers.value.find(d => d.id === idToFind);

    // Return the driver's name if found, otherwise return the original ID
    return driver?.name || driverId || '—';
};

const loadHrAction = async () => {
    try {
        // Check permission before loading HR action
        if (!hasViewPermission.value) {
            showErrorAlert("You don't have permission to view HR actions");
            return;
        }

        const response = await axios.get(`${BASE_URL}/abs-violation-actions/hr-actions/index`, {
            params: {
                violation_action_id: violationAction.value.id
            },
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });

        if (response.data.data && response.data.data.length > 0) {
            hrAction.value = response.data.data[0];
            hrActionForm.value = {
                status: hrAction.value.status || 'Pending',
                remarks: hrAction.value.remarks || '',
                document: null
            };
        }

        // Load action history
        await loadActionHistory();
    } catch (error) {
        console.error('Error loading HR action:', error);
        showErrorAlert(error.response?.data?.message || 'Failed to load HR action');
    }
};

const loadActionHistory = async () => {
    try {
        // Check permission before loading action history
        if (!hasViewPermission.value) {
            showErrorAlert("You don't have permission to view action history");
            return;
        }

        const response = await axios.get(`${BASE_URL}/abs-violation-actions/hr-actions/history`, {
            params: {
                violation_action_id: violationAction.value.id
            },
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });
        actionHistory.value = response.data.data || response.data || [];
        console.log('HR Action API response:', response.status);
    } catch (error) {
        console.error('Error loading action history:', error);
        showErrorAlert(error.response?.data?.message || 'Failed to load action history');
    }
};

const submitHrAction = async () => {
    try {
        // Check permission before submitting HR action
        if (!hasCreatePermission.value) {
            showErrorAlert("You don't have permission to create or update HR actions");
            return;
        }

        isSubmitting.value = true;

        const formData = new FormData();
        formData.append('violation_action_id', violationAction.value.id);
        formData.append('status', hrActionForm.value.status);
        formData.append('taken_action_id', typeof hrActionForm.value.taken_action_id === 'object'
            ? hrActionForm.value.taken_action_id.id
            : hrActionForm.value.taken_action_id);
        formData.append('consolidated_amount', hrActionForm.value.consolidated_amount || '');
        formData.append('remarks', hrActionForm.value.remarks);
        if (hrActionForm.value.document) {
            formData.append('document', hrActionForm.value.document);
        }

        console.log('Submitting HR action with data:', formData);

        const url = hrAction.value.id
            ? `${BASE_URL}/abs-violation-actions/hr-actions/store/${hrAction.value.id}`
            : `${BASE_URL}/abs-violation-actions/hr-actions/store`;

        const method = hrAction.value.id ? 'put' : 'post';

        const response = await axios[method](url, formData, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id,
                'Content-Type': 'multipart/form-data'
            }
        });
        showSuccessAlert(`HR action ${hrAction.value.id ? 'updated' : 'created'} successfully`);
        hrAction.value = response.data.data || response.data;

        // Reload data
        await loadHrAction();

        // Reset form
        hrActionForm.value = {
            status: 'Pending',
            taken_action_id: '',
            consolidated_amount: '',
            remarks: '',
            document: null
        };
    } catch (error) {
        console.error('Error submitting HR action:', error);
        showErrorAlert(error.response?.data?.message || 'Error submitting HR action');
    } finally {
        isSubmitting.value = false;
    }
};

const printReport = () => {
    const printWindow = window.open('', '', 'width=900,height=1000');
    if (!printWindow) return;

    const violation = violationAction.value;
    const hr = hrAction.value;

    const getField = (label, value) => {
        return `
            <div class="row">
                <span class="label">${label}:</span>
                <span class="value">${value || 'N/A'}</span>
            </div>
        `;
    };

    printWindow.document.write(`
        <html>
            <head>
                <title>Violation Report</title>
                <style>
                    body {
                        font-family: Arial, sans-serif;
                        padding: 30px;
                        font-size: 14px;
                    }
                    .header {
                        text-align: center;
                        margin-bottom: 30px;
                    }
                    .header .title {
                        font-size: 24px;
                        font-weight: bold;
                        color: red;
                    }
                    .section {
                        margin-bottom: 25px;
                        border: 1px solid #ccc;
                        border-radius: 6px;
                        padding: 15px;
                    }
                    .section-title {
                        font-size: 18px;
                        font-weight: bold;
                        margin-bottom: 10px;
                        text-decoration: underline;
                    }
                    .row {
                        display: flex;
                        justify-content: space-between;
                        margin-bottom: 8px;
                    }
                    .label {
                        font-weight: bold;
                        flex: 0 0 35%;
                    }
                    .value {
                        flex: 1;
                    }
                    .footer {
                        margin-top: 40px;
                        display: flex;
                        justify-content: space-between;
                        gap: 40px;
                        font-size: 13px;
                    }
                    .signature-block {
                        text-align: center;
                    }
                    .signature-line {
                        margin-top: 30px;
                        border-top: 1px solid #000;
                        width: 200px;
                        margin-left: auto;
                        margin-right: auto;
                    }
                    .document-preview {
                        margin-top: 20px;
                    }
                    @media print {
                        .print-actions {
                            display: none;
                        }
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    <div class="title">ABOOD BUS SERVICE LIMITED</div>
                    <div>P.O Box 127 Msamvu Morogoro, Tanzania</div>
                    <div>Mob: +255 (0) 23 261 3589</div>
                </div>

                <div class="section">
                    <div class="section-title">Bus Details</div>
                    ${getField('Bus Registration', violation.bus_registration)}
                    ${getField('Conductor', violation.conductor_id)}
                    ${getField('Route', violation.route)}
                    ${getField('Schedule', violation.schedule)}
                </div>

                <div class="section">
                    <div class="section-title">Violation Details</div>
                    ${getField('Violation Type', violation.violation?.title)}
                    ${getField('Description', violation.violation?.description)}
                    ${getField('Reported At', formatDateTime(violation.created_at))}
                    ${violation.parameters ? Object.entries(violation.parameters).map(([k, v]) => getField(k, v)).join('') : ''}
                    ${getField('Reported By', violation.user?.username)}
                </div>

                <div class="section">
                    <div class="section-title">HR Action</div>
                    ${getField('Status', hr?.status)}
                    ${getField('Consolidated Amount', hr?.consolidated_amount)}
                    ${getField('Remarks', hr?.remarks)}
                </div>

                <div class="footer">
                    <div class="signature-block">
                        Prepared By:<br><strong>${username.value}</strong><br>IT Officer
                    </div>
                    <div class="signature-block">
                        HR
                        <div class="signature-line"></div>
                    </div>
                    <div class="signature-block">
                        Accounts
                        <div class="signature-line"></div>
                    </div>
                    <div class="signature-block">
                        GM / MD
                        <div class="signature-line"></div>
                    </div>
                </div>

                <div class="print-actions" style="margin-top: 40px; text-align: center;">
                    <button onclick="window.print()" style="padding: 10px 20px; font-weight: bold;">Print</button>
                    <button onclick="window.close()" style="padding: 10px 20px; font-weight: bold;">Close</button>
                </div>
            </body>
        </html>
    `);

    printWindow.document.close();
    printWindow.focus();
};

const cancelHrAction = () => {
    router.go(-1);
};

onMounted(async () => {
    isLoading.value = true;
    try {
        await Promise.all([
            loadViolationAction(),
            loadDrivers(),
            fetchActionsTaken()
        ]);
    } catch (error) {
        console.error('Error during initial load:', error);
        showErrorAlert('Failed to load initial data');
    } finally {
        isLoading.value = false;
    }
});
</script>

<style scoped>
.layout-px-spacing {
    min-height: calc(100vh - 170px) !important;
}

.timeline {
    position: relative;
    padding-left: 1rem;
}

.timeline-item {
    position: relative;
    padding-bottom: 1.5rem;
    padding-left: 1.5rem;
    border-left: 1px solid #dee2e6;
}

.timeline-point {
    position: absolute;
    left: -0.5rem;
    top: 0;
    width: 1rem;
    height: 1rem;
    border-radius: 50%;
    background-color: #4e73df;
}

.timeline-content {
    padding-bottom: 1rem;
}

.timeline-date {
    font-size: 0.8rem;
    color: #6c757d;
}

.timeline-title {
    font-weight: bold;
    margin-bottom: 0.5rem;
}

.timeline-text {
    font-size: 0.9rem;
}

.card-header {
    padding: 1rem 1.25rem;
}

.table th {
    width: 30%;
}

.gap-2 {
    gap: 0.5rem;
}

.btn-sm {
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
}
</style>